/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import update from 'immutability-helper';
import { tableActions, baseTableReducer } from 'carbon-addons-iot-react';

import {
  GET_INTERFACES,
  GET_INTERFACES_SUCCESS,
  GET_INTERFACES_FAILURE,
} from 'interfacesActionTypes';

const getInitialTableState = () => ({
  instanceId: 'liLibraryTable',

  /* An array of device types objects in the form returned by
   * the devices API + an additional "id" column (`${typeId}:${deviceId}`)
   * required by the table component.
   *
   * This is mapped into renderable table data in the render method of DevicesTableContainer
   * It is also used to populate values for other components that need device type data (e.g. DeviceTableDetail)
   */
  data: [],

  shouldApplyDynamicOrdering: true,

  view: {
    filters: [],
    pagination: {
      pageSizes: [5, 10, 20],
      pageSize: 10,
      page: 1,
      totalItems: 0,
    },
    table: {
      isSelectAllSelected: false,
      sort: undefined,
      selectedIds: [],
      expandedIds: [],

      // TODO: how to handle this now that tableColumns no longer in state?
      // ordering: tableColumns.map((i, idx) => ({
      //   columnId: i.id,
      //   isHidden: false, // idx > 3,
      // })),
      loadingState: {
        isLoading: false,
        rowCount: 10, // page size
      },

      rowActions: [],
    },
    toolbar: {
      search: {
        defaultValue: undefined,
      },
    },
  },

  initialTableSearchDefaultValue: undefined,
  deletingLogicalInterfaceId: undefined,
});

export const reducer = (state = getInitialTableState(), action) => {
  switch (action.type) {
    case GET_INTERFACES:
      return update(state, {
        view: {
          table: {
            loadingState: {
              isLoading: { $set: true },
              error: { $set: null },
            },
          },
        },
      });
    case GET_INTERFACES_SUCCESS: {
      return update(state, {
        data: { $set: action.payload },
        view: {
          pagination: {
            totalItems: { $set: action.payload.meta.total_rows },
          },
          table: {
            loadingState: {
              isLoading: { $set: false },
              error: { $set: null },
            },
          },
        },
      });
    }

    case GET_INTERFACES_FAILURE: {
      return update(state, {
        view: {
          table: {
            loadingState: {
              isLoading: { $set: false },
              error: { $set: action.payload },
            },
          },
        },
      });
    }

    case tableActions.TABLE_SEARCH_APPLY:
      if (typeof action.instanceId === 'string' && action.instanceId !== state.instanceId) {
        return state;
      }
      return update(state, {
        initialTableSearchDefaultValue: { $set: action.payload },
      });

    default: // no-op - might be a table action
  }

  // To support instance matching (i.e. multiple table states in one store), just ensure
  // that a instanceId is provided on state passed to baseTableReducer.  If no instanceId
  // is provided, all matching actions will be processed by this table reducer.
  if (typeof action.instanceId === 'string' && action.instanceId !== state.instanceId) {
    return state;
  }

  switch (action.type) {
    case tableActions.TABLE_SEARCH_APPLY: {
      // reset pagination page then apply base table reducer
      const statePrime = update(state, {
        view: {
          pagination: {
            page: { $set: 1 },
            totalItems: { $set: 0 },
          },
        },
      });

      return update(statePrime, {
        $set: baseTableReducer(state, action),
      });
    }

    case tableActions.TABLE_PAGE_CHANGE:
    case tableActions.TABLE_ACTION_CANCEL:
    case tableActions.TABLE_ACTION_APPLY:
    case tableActions.TABLE_ROW_CLICK:
    case tableActions.TABLE_COLUMN_SORT:
    case tableActions.TABLE_ROW_SELECT_ALL:
    case tableActions.TABLE_ROW_SELECT:
    case tableActions.TABLE_TOOLBAR_TOGGLE:
    case tableActions.TABLE_FILTER_APPLY:
    case tableActions.TABLE_FILTER_CLEAR:
    case tableActions.TABLE_EMPTY_STATE_ACTION:
    case tableActions.TABLE_COLUMN_ORDER:
    case tableActions.TABLE_ROW_EXPAND:
      // apply the base table reducer (no further action required in these cases)
      return update(state, {
        $set: baseTableReducer(state, action),
      });

    default:
      return state;
  }
};

export const selectors = {
  getInterfacesTable: state => get(state, 'interfaces.table'),
  getInterfacesTableInstanceId: state => get(state, 'interfaces.table.instanceId'),
  getInterfacesTableView: state => get(state, 'interfaces.table.view'),
  getInterfacesTableFilters: state => get(state, 'interfaces.table.filters'),
  getInterfaces: state => get(state, 'interfaces.table.data.results') || [],
  getInitialTableSearchDefaultValue: state =>
    get(state, 'interfaces.table.initialTableSearchDefaultValue'),
};
